import React, { useEffect, useRef } from 'react';
import Hero from '../components/sections/Hero';
import FeaturedProjects from '../components/sections/FeaturedProjects';
import ContactCTA from '../components/sections/ContactCTA';
import { BuildingIcon, DropletIcon, ScissorsIcon, TruckIcon, FactoryIcon, CheckIcon, AwardIcon, TargetIcon, HeartIcon, PenToolIcon, RefreshCwIcon, ClipboardListIcon, HomeIcon, KeyIcon, WrenchIcon, CompassIcon } from 'lucide-react';
import Button from '../components/ui/Button';
const Home = () => {
  const sectionRefs = {
    about: useRef(null),
    mission: useRef(null),
    services: useRef(null),
    stats: useRef(null),
    why: useRef(null)
  };
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target.classList.contains('section-hidden')) {
            entry.target.classList.remove('section-hidden');
            entry.target.classList.add('animate-fadeIn');
          }
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });
    Object.values(sectionRefs).forEach(ref => {
      if (ref.current) {
        ref.current.classList.add('section-hidden');
        observer.observe(ref.current);
      }
    });
    return () => {
      Object.values(sectionRefs).forEach(ref => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);
  return <div className="w-full" data-id="element-429">
      <Hero data-id="element-430" />
      {/* About Section */}
      <section className="py-20" ref={sectionRefs.about} data-id="element-431">
        <div className="container mx-auto px-4 md:px-6 transition-opacity duration-1000" data-id="element-432">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-433">
            <div data-id="element-434">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a2e1a] mb-6" data-id="element-435">
                About Us
              </h2>
              <p className="text-lg text-gray-700 mb-4" data-id="element-436">
                Socrates & Richard is a dynamic construction company with over a
                decade experience offering comprehensive solutions in
                construction, real estate, engineering, and property
                development. Our services cover every stage of the project —
                from architectural design and structural planning to finishing,
                furnishing, and facility management.
              </p>
              <p className="text-lg text-gray-700 mb-4" data-id="element-437">
                As a privately owned firm, our mission is to deliver authentic,
                high-quality building and real estate services that exceed
                client expectations. Backed by the expertise and experience of
                our skilled professionals, we ensure excellence, efficiency, and
                innovation in every project.
              </p>
              <p className="text-lg text-gray-700 mb-4" data-id="element-438">
                With branches in Abuja, Port Harcourt, Lagos, and Edo State,
                Socrates & Richard continues to expand its footprint across
                Nigeria, building trust and transforming spaces that stand the
                test of time.
              </p>
              <Button to="/about" variant="primary" data-id="element-439">
                Learn More About Us
              </Button>
            </div>
            <div className="relative transform transition-transform duration-700 hover:scale-105" data-id="element-440">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/ksFwscNdhK6ddZHmFijTWo/image_.jpg" alt="Modern residential complex" className="rounded-lg shadow-xl w-full" data-id="element-441" />
              <div className="absolute -bottom-6 -left-6 bg-[#95c11f] text-white p-4 rounded-lg shadow-lg" data-id="element-442">
                <p className="text-xl font-bold" data-id="element-443">15+</p>
                <p className="text-sm" data-id="element-444">Years Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Mission, Vision, Values */}
      <section className="py-20 bg-gray-50" ref={sectionRefs.mission} data-id="element-445">
        <div className="container mx-auto px-4 md:px-6 transition-opacity duration-1000" data-id="element-446">
          <div className="text-center mb-16" data-id="element-447">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2e1a] mb-4" data-id="element-448">
              Mission, Vision & Values
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-449">
              The principles that guide our work and relationships
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-450">
            <div className="bg-white rounded-lg shadow-md p-8 text-center transform transition-all duration-500 hover:-translate-y-2 hover:shadow-xl" data-id="element-451">
              <div className="inline-block p-4 bg-[#e9f5cc] rounded-full text-[#95c11f] mb-6" data-id="element-452">
                <TargetIcon size={32} data-id="element-453" />
              </div>
              <h3 className="text-2xl font-bold text-[#1a2e1a] mb-4" data-id="element-454">
                Our Mission
              </h3>
              <p className="text-gray-700" data-id="element-455">
                We shall ensure that our customer's specific desirable
                requirements are exceptionally met using our modernized
                equipments, highly skilled employees, innovation and the finest
                quality materials to leave a lasting reputable impact.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-8 text-center transform transition-all duration-500 hover:-translate-y-2 hover:shadow-xl" data-id="element-456">
              <div className="inline-block p-4 bg-[#e9f5cc] rounded-full text-[#95c11f] mb-6" data-id="element-457">
                <AwardIcon size={32} data-id="element-458" />
              </div>
              <h3 className="text-2xl font-bold text-[#1a2e1a] mb-4" data-id="element-459">
                Our Vision
              </h3>
              <p className="text-gray-700" data-id="element-460">
                We are passionate about being the leading and most acknowledged
                company highly acclaimed for delivering distinctively
                tailor-made, unique and authentic construction and real estate
                services to our customers in Nigeria and across the globe.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-8 text-center transform transition-all duration-500 hover:-translate-y-2 hover:shadow-xl" data-id="element-461">
              <div className="inline-block p-4 bg-[#e9f5cc] rounded-full text-[#95c11f] mb-6" data-id="element-462">
                <HeartIcon size={32} data-id="element-463" />
              </div>
              <h3 className="text-2xl font-bold text-[#1a2e1a] mb-4" data-id="element-464">
                Our Core Values
              </h3>
              <p className="text-gray-700" data-id="element-465">
                Integrity | Professionalism | Excellence | Innovation | Customer
                Satisfaction
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Why Choose Us */}
      <section className="py-20" ref={sectionRefs.why} data-id="element-466">
        <div className="container mx-auto px-4 md:px-6 transition-opacity duration-1000" data-id="element-467">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-468">
            <div className="transform transition-transform duration-700 hover:scale-105" data-id="element-469">
              <img src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Construction site" className="rounded-lg shadow-xl w-full" data-id="element-470" />
            </div>
            <div data-id="element-471">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a2e1a] mb-6" data-id="element-472">
                Why Choose Us
              </h2>
              <ul className="space-y-4" data-id="element-473">
                <li className="flex items-start transform transition-all duration-500 hover:-translate-x-2" data-id="element-474">
                  <div className="bg-[#e9f5cc] p-1 rounded-full text-[#95c11f] mr-4 mt-1" data-id="element-475">
                    <CheckIcon size={20} data-id="element-476" />
                  </div>
                  <div data-id="element-477">
                    <h3 className="text-xl font-bold text-[#1a2e1a] mb-1" data-id="element-478">
                      Timely & Efficient Service
                    </h3>
                    <p className="text-gray-700" data-id="element-479">
                      We render timely and efficient services to meet our
                      clients' deadlines.
                    </p>
                  </div>
                </li>
                <li className="flex items-start transform transition-all duration-500 hover:-translate-x-2" data-id="element-480">
                  <div className="bg-[#e9f5cc] p-1 rounded-full text-[#95c11f] mr-4 mt-1" data-id="element-481">
                    <CheckIcon size={20} data-id="element-482" />
                  </div>
                  <div data-id="element-483">
                    <h3 className="text-xl font-bold text-[#1a2e1a] mb-1" data-id="element-484">
                      Skilled & Efficient Staff
                    </h3>
                    <p className="text-gray-700" data-id="element-485">
                      We work with highly skilled and efficient staff to render
                      excellent services.
                    </p>
                  </div>
                </li>
                <li className="flex items-start transform transition-all duration-500 hover:-translate-x-2" data-id="element-486">
                  <div className="bg-[#e9f5cc] p-1 rounded-full text-[#95c11f] mr-4 mt-1" data-id="element-487">
                    <CheckIcon size={20} data-id="element-488" />
                  </div>
                  <div data-id="element-489">
                    <h3 className="text-xl font-bold text-[#1a2e1a] mb-1" data-id="element-490">
                      Modern Technology
                    </h3>
                    <p className="text-gray-700" data-id="element-491">
                      We make use of modern technology to deliver high quality
                      real estate and construction services.
                    </p>
                  </div>
                </li>
                <li className="flex items-start transform transition-all duration-500 hover:-translate-x-2" data-id="element-492">
                  <div className="bg-[#e9f5cc] p-1 rounded-full text-[#95c11f] mr-4 mt-1" data-id="element-493">
                    <CheckIcon size={20} data-id="element-494" />
                  </div>
                  <div data-id="element-495">
                    <h3 className="text-xl font-bold text-[#1a2e1a] mb-1" data-id="element-496">
                      Innovative Solutions
                    </h3>
                    <p className="text-gray-700" data-id="element-497">
                      We provide innovative services that set us apart from the
                      competition.
                    </p>
                  </div>
                </li>
                <li className="flex items-start transform transition-all duration-500 hover:-translate-x-2" data-id="element-498">
                  <div className="bg-[#e9f5cc] p-1 rounded-full text-[#95c11f] mr-4 mt-1" data-id="element-499">
                    <CheckIcon size={20} data-id="element-500" />
                  </div>
                  <div data-id="element-501">
                    <h3 className="text-xl font-bold text-[#1a2e1a] mb-1" data-id="element-502">
                      Integrity Based Practice
                    </h3>
                    <p className="text-gray-700" data-id="element-503">
                      We run an integrity based practice and give customers
                      value for their money.
                    </p>
                  </div>
                </li>
                <li className="flex items-start transform transition-all duration-500 hover:-translate-x-2" data-id="element-504">
                  <div className="bg-[#e9f5cc] p-1 rounded-full text-[#95c11f] mr-4 mt-1" data-id="element-505">
                    <CheckIcon size={20} data-id="element-506" />
                  </div>
                  <div data-id="element-507">
                    <h3 className="text-xl font-bold text-[#1a2e1a] mb-1" data-id="element-508">
                      Safety Focused
                    </h3>
                    <p className="text-gray-700" data-id="element-509">
                      We are dedicated to the safety of each and every one of
                      our workers, as they are the greatest assets of the
                      company.
                    </p>
                  </div>
                </li>
                <li className="flex items-start transform transition-all duration-500 hover:-translate-x-2" data-id="element-510">
                  <div className="bg-[#e9f5cc] p-1 rounded-full text-[#95c11f] mr-4 mt-1" data-id="element-511">
                    <CheckIcon size={20} data-id="element-512" />
                  </div>
                  <div data-id="element-513">
                    <h3 className="text-xl font-bold text-[#1a2e1a] mb-1" data-id="element-514">
                      Socially Responsible
                    </h3>
                    <p className="text-gray-700" data-id="element-515">
                      We are a socially responsible and responsive organisation
                      that seeks to give back to society.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      {/* Services Overview */}
      <section className="py-20 bg-gray-100" ref={sectionRefs.services} data-id="element-516">
        <div className="container mx-auto px-4 md:px-6 transition-opacity duration-1000" data-id="element-517">
          <div className="text-center mb-12" data-id="element-518">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2e1a] mb-4" data-id="element-519">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-520">
              Comprehensive construction and real estate solutions
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-521">
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2" data-id="element-522">
              <div className="text-[#95c11f] mb-4" data-id="element-523">
                <BuildingIcon size={36} data-id="element-524" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#1a2e1a]" data-id="element-525">
                Construction Services
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-526">
                Quality work is accomplished through careful planning,
                dedication, skilled and superior execution by our well trained
                and experienced team.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2" data-id="element-527">
              <div className="text-[#95c11f] mb-4" data-id="element-528">
                <PenToolIcon size={36} data-id="element-529" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#1a2e1a]" data-id="element-530">
                Design & Building Services
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-531">
                We deliver accurate, perfect designs and building services with
                significant relationships with clients and engineering firms.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2" data-id="element-532">
              <div className="text-[#95c11f] mb-4" data-id="element-533">
                <RefreshCwIcon size={36} data-id="element-534" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#1a2e1a]" data-id="element-535">
                Rehabilitation & Renovation
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-536">
                We are committed to protecting the environment and refurbishing
                properties to meet client's ambience impression and
                expectations.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2" data-id="element-537">
              <div className="text-[#95c11f] mb-4" data-id="element-538">
                <ClipboardListIcon size={36} data-id="element-539" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#1a2e1a]" data-id="element-540">
                Project Management
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-541">
                We ensure the success of projects from scoping through design,
                construction, commissioning and moving in with careful planning.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2" data-id="element-542">
              <div className="text-[#95c11f] mb-4" data-id="element-543">
                <HomeIcon size={36} data-id="element-544" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#1a2e1a]" data-id="element-545">
                Property Development
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-546">
                We provide specialist services including sales or lease of
                funded properties like office-buildings and warehouses.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2" data-id="element-547">
              <div className="text-[#95c11f] mb-4" data-id="element-548">
                <KeyIcon size={36} data-id="element-549" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#1a2e1a]" data-id="element-550">
                Real Estate Services
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-551">
                We work with commercial and residential builders, developers,
                and contractors to exceed client expectations.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2" data-id="element-552">
              <div className="text-[#95c11f] mb-4" data-id="element-553">
                <WrenchIcon size={36} data-id="element-554" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#1a2e1a]" data-id="element-555">
                Property Management
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-556">
                We offer continuous coverage for maintenance and management of
                building facilities with all necessary support.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2" data-id="element-557">
              <div className="text-[#95c11f] mb-4" data-id="element-558">
                <CompassIcon size={36} data-id="element-559" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#1a2e1a]" data-id="element-560">
                General Engineering Services
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-561">
                Specialized engineers help with roads, bridges, pipeline
                installation, sewage systems, and railway construction.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2 flex flex-col justify-between" data-id="element-562">
              <div data-id="element-563">
                <h3 className="text-xl font-bold mb-3 text-[#1a2e1a]" data-id="element-564">
                  Discover All Our Services
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-565">
                  Explore our full range of services and how we can help with
                  your specific project needs.
                </p>
              </div>
              <Button to="/services" variant="outline" data-id="element-566">
                View All Services
              </Button>
            </div>
          </div>
        </div>
      </section>
      <FeaturedProjects data-id="element-567" />
      {/* Stats Section */}
      <section className="py-20" ref={sectionRefs.stats} data-id="element-568">
        <div className="container mx-auto px-4 md:px-6 transition-opacity duration-1000" data-id="element-569">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-570">
            <div className="text-center transform transition-all duration-500 hover:scale-110" data-id="element-571">
              <p className="text-4xl font-bold text-[#95c11f] mb-2 counter" data-id="element-572">
                75+
              </p>
              <p className="text-gray-600" data-id="element-573">Projects Completed</p>
            </div>
            <div className="text-center transform transition-all duration-500 hover:scale-110" data-id="element-574">
              <p className="text-4xl font-bold text-[#95c11f] mb-2 counter" data-id="element-575">
                15+
              </p>
              <p className="text-gray-600" data-id="element-576">Years Experience</p>
            </div>
            <div className="text-center transform transition-all duration-500 hover:scale-110" data-id="element-577">
              <p className="text-4xl font-bold text-[#95c11f] mb-2 counter" data-id="element-578">
                5+
              </p>
              <p className="text-gray-600" data-id="element-579">Professional Team</p>
            </div>
            <div className="text-center transform transition-all duration-500 hover:scale-110" data-id="element-580">
              <p className="text-4xl font-bold text-[#95c11f] mb-2 counter" data-id="element-581">
                100%
              </p>
              <p className="text-gray-600" data-id="element-582">Client Satisfaction</p>
            </div>
          </div>
        </div>
      </section>
      <ContactCTA data-id="element-583" />
    </div>;
};
export default Home;